/**
 * UpcomingEventCard Component
 *
 * Single event card matching calendar agenda view styling.
 * Used in the Upcoming section on the home screen.
 */

import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { SimpleAvatar } from '@/components/shared/SimpleAvatar';
import { Colors, ModernPalette } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { getBodyFont } from '@/constants/typography';
import type { CalendarEvent } from '@/types/events';

type UpcomingEventCardProps = {
  event: CalendarEvent;
  onPress?: () => void;
  avatarImageUri?: string | null;
  avatarColor?: string;
  avatarName?: string;
  tintedBackground?: string;
  isPast?: boolean;
};

/**
 * Format time string (HH:MM) to 12-hour format with separate time and period
 */
function parseTimeDisplay(timeStr: string | null | undefined): { time: string; period: string } {
  if (!timeStr) {
    return { time: 'All', period: 'day' };
  }

  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;

  return {
    time: `${displayHours}:${minutes.toString().padStart(2, '0')}`,
    period,
  };
}

/**
 * UpcomingEventCard displays a single calendar event in agenda card style.
 */
export function UpcomingEventCard({ event, onPress, avatarImageUri, avatarColor, avatarName, tintedBackground, isPast }: UpcomingEventCardProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const defaultBackground = colorScheme === 'light' ? ModernPalette.white : palette.backgroundLight;
  const cardOpacity = isPast ? 0.45 : 1.0;

  const { time, period } = parseTimeDisplay(event.startTime);
  const isAllDay = !event.startTime;

  const accentColor = avatarColor ?? palette.primary;

  const CardWrapper = onPress ? TouchableOpacity : View;

  return (
    <CardWrapper
      style={[
        styles.card,
        { opacity: cardOpacity, borderWidth: 1, borderColor: accentColor },
        isAllDay
          ? { backgroundColor: tintedBackground ?? defaultBackground }
          : { backgroundColor: 'transparent' },
      ]}
      {...(onPress ? { onPress, activeOpacity: 0.7 } : {})}
    >
      {/* Time section (left) */}
      <View style={styles.timeSection}>
        <Text style={[styles.timeValue, { color: palette.text }]}>{time}</Text>
        <Text style={[styles.timePeriod, { color: palette.textMuted }]}>{period}</Text>
      </View>

      {/* Vertical dashed separator */}
      <View style={[styles.separator, { borderColor: palette.border }]} />

      {/* Content section (middle) */}
      <View style={styles.contentSection}>
        <Text
          style={[styles.title, { color: palette.text }]}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {event.title}
        </Text>
      </View>

      {/* Avatar section (right) */}
      <View style={styles.avatarSection}>
        <View
          style={[
            styles.avatarRing,
            { borderColor: accentColor },
          ]}
        >
          <SimpleAvatar
            name={avatarName ?? event.title}
            size={28}
            color={avatarColor ?? palette.primary}
            imageUri={avatarImageUri ?? undefined}
          />
        </View>
      </View>
    </CardWrapper>
  );
}

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: BorderRadius.sm,
    paddingHorizontal: Spacing.sm,
    paddingVertical: Spacing.sm,
  },
  timeSection: {
    width: 45,
    alignItems: 'center',
  },
  timeValue: {
    fontSize: 12,
    fontWeight: '700',
    fontFamily: getBodyFont(),
    lineHeight: 16,
  },
  timePeriod: {
    fontSize: 10,
    fontWeight: '500',
    fontFamily: getBodyFont(),
    lineHeight: 12,
    textTransform: 'uppercase',
  },
  separator: {
    width: 1,
    height: '100%',
    borderLeftWidth: 1,
    borderStyle: 'dashed',
    marginHorizontal: Spacing.xs,
  },
  contentSection: {
    flex: 1,
    marginHorizontal: Spacing.sm,
  },
  title: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: getBodyFont(),
    lineHeight: 16,
  },
  avatarSection: {
    marginLeft: Spacing.sm,
  },
  avatarRing: {
    borderWidth: 2,
    borderRadius: 17,
    padding: 1,
  },
});
